import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Truck } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    if (user.role === "superadmin") return <Navigate to="/admin/workspaces" replace />;
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username, password);
      toast.success("Signed in");
      nav(u.role === "superadmin" ? "/admin/workspaces" : "/");
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const companyName = "Dispatch Pay Calculator";
  const logoSrc = "/dispatch-pay-logo.png";

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      {/* Left — brand */}
      <div className="hidden lg:flex flex-col justify-center p-16 bg-[#0B1A3A] border-r border-slate-800 text-white">
        <img src={logoSrc} alt={companyName}
             className="w-full max-w-md h-auto mb-10"
             data-testid="login-logo-img" />
        <h1 className="text-4xl font-semibold leading-tight">
          One place for driver records, trip miles and every pay run.
        </h1>
        <p className="mt-4 text-slate-300 max-w-md">
          Built for dispatch and payroll desks: log trips, pull them into a driver's pay
          period, apply per-mile, percentage, flat, deduction and reimbursement lines,
          then export payroll — with an audit trail behind every change.
        </p>
        <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
          {[
            ["Drivers", "Profiles, rates, status"],
            ["Trips", "Bulk entry, filters, totals"],
            ["Pay Calculator", "Free-form line items"],
            ["Reports", "Live period roll-ups"],
            ["Payroll Export", "CSV + QuickBooks path"],
            ["Audit Trail", "Field-level history"],
          ].map(([t, s]) => (
            <div key={t}>
              <div className="text-sm font-semibold text-amber-400">{t}</div>
              <div className="text-xs text-slate-400">{s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — sign in */}
      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-2 text-slate-900 mb-5">
              <ShieldCheck size={16} /> <span className="font-semibold">Sign in</span>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Username</Label>
                <Input
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</Label>
                <Input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </div>
          <div className="mt-6 text-center text-sm text-slate-600">
            New here?{" "}
            <Link to="/signup" className="text-blue-700 font-semibold" data-testid="signup-link">
              Create a workspace
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
